import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Permission, RoleSummary } from '../api/permissions.api.types.ts';
import { isPermissionGuardEnabled } from '../utils/feature-flag.ts';

export const PERMISSIONS_STORE_NAME = 'permissions';

const toPermissionKey = ({ permission, action }: Permission): string =>
  `${permission}:${action}`;

export const usePermissionsStore = defineStore(PERMISSIONS_STORE_NAME, () => {
  const permissions = ref<Permission[]>([]);
  const role = ref<RoleSummary | null>(null);
  const lastFetchedAt = ref<number | null>(null);

  const setPermissions = (list: Permission[]) => {
    permissions.value = [...list];
    lastFetchedAt.value = Date.now();
  };

  const setRole = (next: RoleSummary | null) => {
    role.value = next;
  };

  const clearOnLogout = () => {
    permissions.value = [];
    role.value = null;
    lastFetchedAt.value = null;
  };

  // O(1) lookup. `hasPermission` is invoked many times per render once
  // components and the router guard / column filters land.
  const permissionsSet = computed(
    () => new Set(permissions.value.map(toPermissionKey)),
  );

  /**
   * Single feature-flag–gated permission check.
   * Flag OFF → always `true` (allow-all UI, ship-before-BE).
   * Flag ON  → check membership in the fetched permission list.
   */
  const hasPermission = computed(() => (key: Permission): boolean => {
    if (!isPermissionGuardEnabled()) return true;
    return permissionsSet.value.has(toPermissionKey(key));
  });

  return {
    permissions,
    role,
    lastFetchedAt,
    setPermissions,
    setRole,
    clearOnLogout,
    hasPermission,
  };
});
